import { useFormik } from "formik";
import IBAN from 'iban';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { cn, Switch, Tooltip } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import ButtonPopover from "../../components/buttons/ButtonPopover";
import ContractTemplateWYSIWYGEditor from "../../components/contract-templates/ContractTemplateWYSIWYGEditor";
import CustomSelect from "../../components/forms/CustomSelect";
import CustomSelectApiHookForm from "../../components/forms/CustomSelectApiHookForms";
import { DatePicker } from "../../components/forms/DatePicker";
import RequiredInput from "../../components/forms/InputHOC";
import PhoneInput from "../../components/forms/PhoneInputWrapper";
import { Loader } from "../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../components/priviledge/PriviledgeProvider";
import useCorporations from "../../hooks/api-calls/useCorporations";
import useFetch from "../../hooks/useFetch";
import useHandleErrors from "../../hooks/useHandleErrors";
import Page from "../../layout/Page/Page";
import FormGroup from "../../layout/shared/form-group";
import { CorporationService } from "../../services/corporation/corporationService";
import { ProductService } from "../../services/product/productService";
import { Corporation } from "../../type/entities/corporation-type";
import { es } from "date-fns/locale/es";
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from "../../utils/editorConversions";
import { validateCifNif } from "../../utils/validatorFunctions";
import FormFooter from "../_layout/_footers/form-footer";
import BanksForm from "./components/BanksForm";

interface CreateFormProps {
    id: string | undefined;
    setNodeId: (value: string | undefined) => void;
    nodeStatus: boolean;
    setNodeStatus: (value: boolean) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    refetch: () => void;
}

const schema = yup.object({
    name: yup.string().required('Campo obligatorio'),
    corporationFunction: yup.string().required('Campo obligatorio'),
    cif: yup.string().test('valid-cif-nif', 'El formato de CIF/NIF no es válido', (value) => {
        if (value !== undefined && value.length > 0) {
            return validateCifNif(value);
        } else {
            return true;
        }
    }).required('Campo obligatorio'),
    iban: yup.string()
        .required('Campo obligatorio')
        .test('iban', 'El formato de IBAN no es válido', (value) => {
            if (!value) return true;
            return IBAN.isValid(value);
        }),

    registeredOffice: yup.string().required('Campo obligatorio'),
    providingCorporationIds: yup.array().of(yup.string()).when('corporationFunction', {
        is: (value: string) => value === 'platform',
        then: (schema) => schema.min(1, 'Debes seleccionar al menos una impartidora').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    organisingCorporationIds: yup.array().of(yup.string()).when('corporationFunction', {
        is: (value: string) => value === 'providing',
        then: (schema) => schema.min(1, 'Debes seleccionar al menos una organizadora').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    representativeName: yup.string().required('Campo obligatorio'),
    representativeTelephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
    representativeEmail: yup.string().email('Email no válido').required('Campo obligatorio'),
    postalCode: yup.string().matches(/^[0-9]{5}$/, 'Código postal no válido').required('Campo obligatorio'),
    province: yup.string().required('Campo obligatorio'),
    city: yup.string().required('Campo obligatorio'),
    email: yup.string().email('Email no válido').notRequired().nullable(),
    secondaryEmail: yup.string().email('Email no válido').notRequired().nullable(),
    telephone: yup.string().notRequired().nullable(),
    constitutionProtocol: yup.string().notRequired().nullable(),
    constitutionNotary: yup.string().notRequired().nullable(),
    constitutionDate: yup.string().notRequired().nullable(),
    book: yup.number().notRequired().nullable(),
    page: yup.number().notRequired().nullable(),
    registrySheet: yup.string().notRequired().nullable(),
    mercantileRegistry: yup.string().notRequired().nullable(),
    representativeCifNif: yup.string().test('valid-cif-nif', 'El formato de CIF/NIF no es válido', (value) => {
        if (value !== undefined && value.length > 0) {
            return validateCifNif(value);
        } else {
            return true;
        }
    }).required('Campo obligatorio'),
    appointmentProtocol: yup.string().notRequired().nullable(),
    appointmentDate: yup.string().notRequired().nullable(),
    totalInvoiceAmountPercentage: yup.number().min(0, 'El porcentaje no puede ser negativo').max(100, 'El porcentaje no puede ser mayor que 100').when('corporationFunction', {
        is: (value: string) => value === 'platform',
        then: (schema) => schema.moreThan(0, 'Debe ser mayor que 0').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    invoiceCodeFormat: yup.string().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    invoiceSequence: yup.number().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.min(0, 'La secuencia debe ser mayor o igual que 0').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    nonStandardInvoiceCodeFormat: yup.string().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    nonStandardInvoiceSequence: yup.number().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.min(0, 'La secuencia debe ser mayor o igual que 0').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    rectificationInvoiceCodeFormat: yup.string().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    rectificationInvoiceSequence: yup.number().when('corporationFunction', {
        is: (value: string) => ['organising', 'providing', 'platform', 'services'].includes(value),
        then: (schema) => schema.min(0, 'La secuencia debe ser mayor o igual que 0').required('Campo obligatorio'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    groupCodeIdentifier: yup.string().nullable().notRequired(),
    contractTemplate: yup.string().nullable().notRequired(),
    banks: yup.array().of(
        yup.object().shape({
            name: yup.string().required('Campo obligatorio'),
            iban: yup.string()
                .required('Campo obligatorio')
                .test('iban', 'El formato de IBAN no es válido', (value) => {
                    if (!value) return true;
                    return IBAN.isValid(value);
                }),
            creditor: yup.string().required('Campo obligatorio'),
            bic: yup.string().required('Campo obligatorio'),
            bankIdentifier: yup.string().required('Campo obligatorio'),
        })
    ),
});

export const corporationFunctionOptions = [
    { value: 'providing', label: 'Impartidora' },
    { value: 'organising', label: 'Organizadora' },
    { value: 'platform', label: 'Plataforma' },
    { value: 'services', label: 'Empresa de servicios' },
];

const CorporationForm: React.FC<CreateFormProps> = ({ id, setNodeId, nodeStatus, setNodeStatus, isOpen, setIsOpen, refetch }) => {

    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { fetchCorporations: fetchOrganisingCorporations } = useCorporations({ active: true, corporation_function: 'organising', parent_corporation_id: 'null' });
    const { fetchCorporations: fetchProvidingCorporations } = useCorporations({ active: true, corporation_function: 'providing' });
    const service = new CorporationService();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [variables, setVariables] = useState<any[]>([]);
    const [contractLogoFile, setContractLogoFile] = useState<File | null>(null);
    const [contractSignatureFile, setContractSignatureFile] = useState<File | null>(null);
    const [contractSealFile, setContractSealFile] = useState<File | null>(null);
    const [bank, setBank] = React.useState<{ name: string, iban: string, creditor: string, bic: string, bankIdentifier: string }>({ name: '', iban: '', creditor: '', bic: '', bankIdentifier: '' });
    const bankRef = useRef(bank);
    bankRef.current = bank;

    const hasPendingContractAssets = !!contractLogoFile || !!contractSignatureFile || !!contractSealFile;

    const [data] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getCorporationById(id as string);
        return response.getResponseData() as Corporation;
    }, [id]));

    const mode = data ? 'Editar' : 'Crear';

    const memoizedContractTemplateData = useMemo(() => {
        if (!data?.contractTemplate) return undefined;

        try {
            return JSON.parse(data.contractTemplate);
        } catch {
            if (isValidHTML(data.contractTemplate)) {
                return convertHTMLToEditorJSON(data.contractTemplate);
            }
            return undefined;
        }
    }, [data?.contractTemplate]);

    const loadContractVariables = async () => {
        try {
            const response = await (new ProductService()).getContractFields();
            const responseData = response.getResponseData()?.data;
            responseData && setVariables(responseData || []);
        } catch (error) {
            toast.error('Error al cargar las variables de plantilla');
        }
    };

    const uploadContractAssets = async (corporationId: string): Promise<boolean> => {
        if (!hasPendingContractAssets) {
            return true;
        }

        try {
            const uploadResponse = await service.updateCorporationContractAssets(corporationId, {
                contractLogo: contractLogoFile,
                contractSignature: contractSignatureFile,
                contractSeal: contractSealFile,
            });
            const uploadResponseData = uploadResponse.getResponseData();

            if (!uploadResponseData.success) {
                handleErrors(uploadResponseData);
                return false;
            }

            setContractLogoFile(null);
            setContractSignatureFile(null);
            setContractSealFile(null);
            return true;
        } catch (error: any) {
            handleErrors(error);
            return false;
        }
    };

    const submit = async (values: any) => {
        setIsLoading(true);

        if (mode === 'Crear') {
            try {
                const response = await service.createCorporation(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    const createdCorporationId = responseData?.data?.corporationId;
                    if (values.corporationFunction === 'organising' && hasPendingContractAssets) {
                        if (!createdCorporationId) {
                            toast.error('No se pudieron subir los documentos de organizadora porque no se recibió el identificador.');
                            setIsLoading(false);
                            return;
                        }

                        const uploaded = await uploadContractAssets(createdCorporationId);
                        if (!uploaded) {
                            setIsLoading(false);
                            return;
                        }
                    }

                    toast.success("Empresa creada correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            try {
                const response = await service.editCorporation(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    if (values.corporationFunction === 'organising' && hasPendingContractAssets) {
                        const uploaded = await uploadContractAssets(values.corporationId);
                        if (!uploaded) {
                            setIsLoading(false);
                            return;
                        }
                    }

                    toast.success("Empresa editada correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const toggleState = async (id: string, state: boolean) => {
        try {
            const response = (await service.toggleCorporationStatus(id, state)).getResponseData();
            if (response.success) {
                setNodeStatus(!state);
                toast.success(`Empresa ${!state ? 'activada' : 'desactivada'} correctamente`);
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            handleErrors(error);
        }
    };

    const handleDelete = async (id: string) => {
        const response = await (await service.deleteCorporation(id)).getResponseData();
        if (response.success) {
            toast.success('Empresa eliminada correctamente');
            setIsOpen(false);
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const formik = useFormik({
        initialValues: {
            corporationId: id ?? '',
            name: data?.name ?? '',
            corporationFunction: data?.corporationFunction ?? '',
            cif: data?.cif ?? '',
            iban: data?.iban ?? '',
            registeredOffice: data?.registeredOffice ?? '',
            parentCorporationName: data?.parentCorporation?.name ?? '',
            providingCorporationIds: data?.providingCorporations?.map((p: any) => p.id) ?? (data?.parentCorporation?.id ? [data.parentCorporation.id] : []),
            organisingCorporationIds: data?.organisingCorporations?.map((o: any) => o.id) ?? [],
            representativeName: data?.representativeName ?? '',
            representativeTelephone: data?.representativeTelephone ?? '',
            representativeEmail: data?.representativeEmail ?? '',
            postalCode: data?.postalCode ?? '',
            province: data?.province ?? '',
            city: data?.city ?? '',
            email: data?.email ?? '',
            secondaryEmail: data?.secondaryEmail ?? '',
            telephone: data?.telephone ?? '',
            constitutionProtocol: data?.constitutionProtocol ?? '',
            constitutionNotary: data?.constitutionNotary ?? '',
            constitutionDate: data?.constitutionDate ? (typeof data.constitutionDate === 'string' ? data.constitutionDate : (data.constitutionDate as any)?.date?.split?.(' ')[0] ?? '') : '',
            book: data?.book ?? 0,
            page: data?.page ?? 0,
            registrySheet: data?.registrySheet ?? '',
            mercantileRegistry: data?.mercantileRegistry ?? '',
            representativeCifNif: data?.representativeCifNif ?? '',
            appointmentProtocol: data?.appointmentProtocol ?? '',
            appointmentDate: data?.appointmentDate ?? '',
            totalInvoiceAmountPercentage: data?.totalInvoiceAmountPercentage ?? 0,
            invoiceCodeFormat: data?.invoiceCodeFormat ?? '',
            invoiceSequence: data?.invoiceSequence ?? 0,
            nonStandardInvoiceCodeFormat: data?.nonStandardInvoiceCodeFormat ?? '',
            nonStandardInvoiceSequence: data?.nonStandardInvoiceSequence ?? 0,
            rectificationInvoiceCodeFormat: data?.rectificationInvoiceCodeFormat ?? '',
            rectificationInvoiceSequence: data?.rectificationInvoiceSequence ?? 0,
            groupCodeIdentifier: data?.groupCodeIdentifier ?? '',
            contractTemplate: data?.contractTemplate ?? null,
            banks: data?.banks ? data.banks.map((b: any) => ({
                name: b.name,
                iban: b.iban,
                creditor: b.creditor,
                bic: b.bic ?? '',
                bankIdentifier: b.bankIdentifier ?? b.bank_identifier ?? ''
            })) : [],
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => {
            const banksToSubmit = [...(values.banks || [])];
            const currentBank = bankRef.current;
            if (currentBank.name && currentBank.iban && currentBank.creditor && currentBank.bic && currentBank.bankIdentifier) {
                banksToSubmit.push(currentBank);
            }
            // legacy compatibility: keep parentCorporationId as first selected providing
            submit({ ...values, banks: banksToSubmit });
        },
    });

    useEffect(() => {
        if (data) {
            formik.setValues({
                corporationId: data.id,
                name: data.name ?? '',
                corporationFunction: data.corporationFunction ?? '',
                cif: data.cif ?? '',
                iban: data.iban ?? '',
                registeredOffice: data.registeredOffice ?? '',
                parentCorporationName: data.parentCorporation?.name ?? '',
                providingCorporationIds: data?.providingCorporations?.map((p: any) => p.id) ?? (data?.parentCorporation?.id ? [data.parentCorporation.id] : []),
                organisingCorporationIds: data.organisingCorporations?.map((o: any) => o.id) ?? [],
                representativeName: data.representativeName ?? '', // only used to display the name in the selector
                representativeTelephone: data.representativeTelephone ?? '',
                representativeEmail: data.representativeEmail ?? '',
                postalCode: data.postalCode ?? '',
                province: data.province ?? '',
                city: data.city ?? '',
                email: data.email ?? '',
                secondaryEmail: data.secondaryEmail ?? '',
                telephone: data.telephone ?? '',
                constitutionProtocol: data.constitutionProtocol ?? '',
                constitutionNotary: data.constitutionNotary ?? '',
                constitutionDate: data.constitutionDate ? (typeof data.constitutionDate === 'string' ? data.constitutionDate : (data.constitutionDate as any)?.date?.split?.(' ')[0] ?? '') : '',
                book: data.book ?? 0,
                page: data.page ?? 0,
                registrySheet: data.registrySheet ?? '',
                mercantileRegistry: data.mercantileRegistry ?? '',
                representativeCifNif: data.representativeCifNif ?? '',
                appointmentProtocol: data.appointmentProtocol ?? '',
                appointmentDate: data.appointmentDate?.date.split(' ')[0] ?? '',
                totalInvoiceAmountPercentage: data.totalInvoiceAmountPercentage ?? 0,
                invoiceCodeFormat: data.invoiceCodeFormat ?? '',
                invoiceSequence: data.invoiceSequence ?? 0,
                nonStandardInvoiceCodeFormat: data.nonStandardInvoiceCodeFormat ?? '',
                nonStandardInvoiceSequence: data.nonStandardInvoiceSequence ?? 0,
                rectificationInvoiceCodeFormat: data.rectificationInvoiceCodeFormat ?? '',
                rectificationInvoiceSequence: data.rectificationInvoiceSequence ?? 0,
                groupCodeIdentifier: data.groupCodeIdentifier ?? '',
                contractTemplate: data.contractTemplate ?? null,
                banks: data?.banks ? data.banks.map((b: any) => ({
                    name: b.name,
                    iban: b.iban,
                    creditor: b.creditor,
                    bic: b.bic ?? '',
                    bankIdentifier: b.bankIdentifier ?? b.bank_identifier ?? ''
                })) : [],
            });
        }
    }, [data]);

    useEffect(() => {
        if (id === undefined) {
            formik.resetForm();
        }
    }, [id]);

    useEffect(() => {
        if (isOpen) {
            loadContractVariables();
        }
    }, [isOpen]);

    useEffect(() => {
        if (formik.values.corporationFunction !== 'providing' && (formik.values.organisingCorporationIds?.length ?? 0) > 0) {
            formik.setFieldValue('organisingCorporationIds', []);
        }

        if (formik.values.corporationFunction !== 'platform' && (formik.values.providingCorporationIds?.length ?? 0) > 0) {
            formik.setFieldValue('providingCorporationIds', []);
        }

        if (formik.values.corporationFunction !== 'organising') {
            formik.setFieldValue('groupCodeIdentifier', '');
            formik.setFieldValue('contractTemplate', null);
            setContractLogoFile(null);
            setContractSignatureFile(null);
            setContractSealFile(null);
        }
    }, [formik.values.corporationFunction]);


    if (isOpen && id && !data) return <Loader />;

    const shouldShowInvoiceConfiguration = ['organising', 'providing', 'platform', 'services'].includes(formik.values.corporationFunction || '');
    const shouldShowParentSelector = formik.values.corporationFunction === 'platform';
    const shouldShowPercentageDistribution = ['providing', 'platform', 'services'].includes(formik.values.corporationFunction || '');
    const shouldShowOrganisingContractTemplate = formik.values.corporationFunction === 'organising';
    const shouldShowOrganisingGroupCodeIdentifier = formik.values.corporationFunction === 'organising';

    const getDocumentLabel = (document: any, fallback: string) => {
        if (!document) return fallback;
        const baseName = document.originalName || document.fileName || fallback;
        const extension = document.extension ? `.${document.extension}` : '';
        return `${baseName}${extension}`;
    };

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-10 grid gap-3 @2xl:gap-9 @3xl:gap-11 font-medium">
                        {id && isOpen && (
                            <div className='grid grid-cols-2 gap-10'>
                                <div className="col-start-2 flex justify-end items-center">
                                    <Tooltip
                                        size="sm"
                                        content={nodeStatus ? 'Desactivar empresa' : 'Activar empresa'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <Switch
                                                id={id}
                                                label={nodeStatus ? 'Activa' : 'Inactiva'}
                                                checked={nodeStatus ? true : false}
                                                onChange={() => toggleState(id, nodeStatus)}
                                                switchKnobClassName='bg-secondary'
                                            />
                                        </div>
                                    </Tooltip>

                                    {userCan('delete_corporation', 'corporation') && (
                                        <ButtonPopover
                                            title='Eliminar empresa'
                                            description='Esto eliminará todo lo que dependa de esta empresa'
                                            onClick={() => handleDelete(id)}
                                            variant='text'
                                            mobileView
                                            btnClassName=" px-4 py-2 gap-2"
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        <FormGroup
                            title="Información General"
                            description="Datos de la empresa fiscal"
                            titleCols="@md:col-span-4"
                            childCols="@md:col-span-12 md:grid-cols-12"
                        >
                            {formik.values.corporationFunction === 'providing' ? (
                                <CustomSelectApiHookForm
                                    key={formik.values.organisingCorporationIds?.join(',')}
                                    id="organisingCorporationIds"
                                    label="Organizadoras a las que da servicio"
                                    isMulti
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        return fetchOrganisingCorporations({ search_text: searchText }) as any;
                                    }}
                                    error={formik.errors.organisingCorporationIds}
                                    minInputLength={2}
                                    containerClassName='md:col-span-6'
                                />
                            ) : shouldShowParentSelector ? (
                                <CustomSelectApiHookForm
                                    key={(formik.values.providingCorporationIds || []).join(',')}
                                    id="providingCorporationIds"
                                    label={"Impartidoras asociadas"}
                                    isMulti
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        if (searchText) {
                                            return fetchProvidingCorporations({ search_text: searchText }) as any;
                                        }
                                        const ids = formik.values.providingCorporationIds || [];
                                        if (ids.length > 0) {
                                            return fetchProvidingCorporations({ corporation_ids: ids }) as any;
                                        }
                                        if (ids.length === 1) {
                                            return fetchProvidingCorporations({ corporation_id: ids[0] }) as any;
                                        }
                                        return fetchProvidingCorporations({ search_text: formik.values.parentCorporationName || '' }) as any;
                                    }}
                                    error={formik.errors.providingCorporationIds as any}
                                    minInputLength={2}
                                    containerClassName='md:col-span-6'
                                    initialOption={null}
                                />
                            ) : null}

                            <RequiredInput
                                id='name'
                                label="Nombre"
                                formik={formik}
                                className="md:col-span-6"
                            />

                            <RequiredInput
                                id='cif'
                                label="CIF/NIF"
                                formik={formik}
                                className="md:col-span-3"
                            />

                            <CustomSelect
                                required
                                id='corporationFunction'
                                label="Función"
                                containerClassName='md:col-span-3'
                                value={corporationFunctionOptions.find((g: any) => g.value === formik.values.corporationFunction)}
                                options={corporationFunctionOptions}
                                onChange={(e: any) => {
                                    const nextFunction = e?.value ?? '';
                                    formik.setFieldValue('corporationFunction', nextFunction);

                                    if(nextFunction === 'organising')
                                    {
                                        formik.setFieldValue('parentCorporationId', null);
                                        formik.setFieldValue('parentCorporationName', '');
                                        formik.setFieldValue('organisingCorporationIds', []);
                                    }

                                    if(nextFunction === 'providing')
                                    {
                                        formik.setFieldValue('parentCorporationId', null);
                                        formik.setFieldValue('parentCorporationName', '');
                                    }

                                    if(['platform', 'services'].includes(nextFunction))
                                    {
                                        formik.setFieldValue('organisingCorporationIds', []);
                                    }

                                    if(nextFunction !== 'platform')
                                    {
                                        formik.setFieldValue('providingCorporationIds', []);
                                    }
                                }}
                                error={formik.touched ? (formik.errors.corporationFunction as string ?? undefined) : undefined}
                            />

                            <RequiredInput
                                id='iban'
                                label="IBAN"
                                formik={formik}
                                className="md:col-span-6"
                            />

                            <RequiredInput
                                id='registeredOffice'
                                label="Domilicio Social"
                                formik={formik}
                                className="md:col-span-6"
                            />

                            <RequiredInput
                                id='postalCode'
                                label="Código Postal"
                                formik={formik}
                                className="md:col-span-3"
                            />

                            <RequiredInput
                                id='province'
                                label="Provincia"
                                formik={formik}
                                className="md:col-span-3"
                            />

                            <RequiredInput
                                id='city'
                                label="Ciudad"
                                formik={formik}
                                className="md:col-span-3"
                            />

                            <RequiredInput
                                id='email'
                                label="Email"
                                type={"email"}
                                formik={formik}
                                required={false}
                                className="md:col-span-5"
                            />

                            <RequiredInput
                                id='secondaryEmail'
                                label="Email Secundario"
                                type={"email"}
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <PhoneInput
                                id='telephone'
                                label="Teléfono"
                                onChange={e => { formik.setFieldValue('telephone', e) }}
                                value={formik.values.telephone}
                                error={formik.errors.telephone as string}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='constitutionProtocol'
                                label="Protocolo de Constitución"
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='constitutionNotary'
                                label="Notario de Constitución"
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <div className="md:col-span-4">
                                <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Fecha de Constitución</span>
                                <DatePicker
                                    isClearable
                                    monthsShown={1}
                                    placeholderText="Selecciona fecha"
                                    dateFormat="dd/MM/yyyy"
                                    selected={formik.values.constitutionDate ? new Date(formik.values.constitutionDate + 'T12:00:00') : null}
                                    onChange={(date: Date | null) => formik.setFieldValue('constitutionDate', date ? date.toISOString().split('T')[0] : '')}
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={15}
                                    scrollableYearDropdown
                                    inputProps={{
                                        inputClassName: 'h-9 [&_input]:text-ellipsis',
                                    }}
                                    className="w-full"
                                    locale={es}
                                    popperPlacement="bottom-start"
                                />
                                {formik.touched.constitutionDate && formik.errors.constitutionDate && (
                                    <p className="mt-1 text-sm text-red-500">{String(formik.errors.constitutionDate)}</p>
                                )}
                            </div>

                            <RequiredInput
                                id='book'
                                label="Tomo"
                                formik={formik}
                                required={false}
                                className="md:col-span-2"
                            />

                            <RequiredInput
                                id='page'
                                label="Folio"
                                formik={formik}
                                required={false}
                                className="md:col-span-2"
                            />

                            <RequiredInput
                                id='registrySheet'
                                label="Hoja de Registro"
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='mercantileRegistry'
                                label="Registro Mercantil"
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='appointmentProtocol'
                                label="Protocolo de Nombramiento"
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='appointmentDate'
                                label="Fecha de Nombramiento"
                                type={"date"}
                                formik={formik}
                                required={false}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='totalInvoiceAmountPercentage'
                                label="Porcentaje Total Facturación"
                                type={"number"}
                                formik={formik}
                                required={false}
                                className={cn("md:col-span-4", { 'hidden': !shouldShowPercentageDistribution })}
                            />

                            <RequiredInput
                                id='invoiceCodeFormat'
                                label="Formato Código Factura"
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='invoiceSequence'
                                label="Secuencia de Factura"
                                type={"number"}
                                min={0}
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='nonStandardInvoiceCodeFormat'
                                label="Formato Código Factura Atípica"
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                contentTooltipText="Formato para la numeración de facturas atípicas. Usa {{NUMBER}}, {{YEAR}}, {{MONTH}}. Ej: AT{{NUMBER}}-{{YEAR}}"
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='nonStandardInvoiceSequence'
                                label="Secuencia de Factura Atípica"
                                type={"number"}
                                min={0}
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='rectificationInvoiceCodeFormat'
                                label="Formato Código Factura Rectificativa"
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='rectificationInvoiceSequence'
                                label="Secuencia de Factura Rectificativa"
                                type={"number"}
                                min={0}
                                formik={formik}
                                required={shouldShowInvoiceConfiguration}
                                className={cn("md:col-span-6", { 'hidden': !shouldShowInvoiceConfiguration })}
                            />

                            <RequiredInput
                                id='groupCodeIdentifier'
                                label="Identificador de empresa"
                                formik={formik}
                                required={false}
                                contentTooltipText="Este campo se usará para identificar la organizadora en los códigos de grupo."
                                className={cn("md:col-span-4", { 'hidden': !shouldShowOrganisingGroupCodeIdentifier })}
                            />
                        </FormGroup>

                        <FormGroup
                            title="Representante"
                            description="Datos del representante legal"
                            titleCols="@md:col-span-4"
                            childCols="@md:col-span-10 md:grid-cols-12"
                            className={cn({ 'pt-0': shouldShowOrganisingContractTemplate })}
                        >
                            <RequiredInput
                                id='representativeCifNif'
                                label="CIF/NIF del Representante"
                                formik={formik}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='representativeName'
                                label="Nombre del Representante"
                                formik={formik}
                                className="md:col-span-4"
                            />

                            <PhoneInput
                                id='representativeTelephone'
                                label="Teléfono del Representante"
                                onChange={e => { formik.setFieldValue('representativeTelephone', e) }}
                                value={formik.values.representativeTelephone}
                                error={formik.errors.representativeTelephone as string}
                                className="md:col-span-4"
                            />

                            <RequiredInput
                                id='representativeEmail'
                                label="Email del Representante"
                                formik={formik}
                                className="md:col-span-4"
                            />
                        </FormGroup>

                        {shouldShowOrganisingContractTemplate && (
                            <FormGroup
                                title="Plantilla de contrato organizadora"
                                description="Plantilla y recursos para contratos de cliente cuando el producto no requiera contrato propio"
                                titleCols="@md:col-span-4"
                                childCols="@md:col-span-12 md:grid-cols-12"
                            >
                                <div className="md:col-span-12">
                                    <ContractTemplateWYSIWYGEditor
                                        key={`corp-contract-template-${formik.values.corporationId || 'new'}`}
                                        type='contract'
                                        initialData={memoizedContractTemplateData}
                                        onChange={(editorData) => formik.setFieldValue('contractTemplate', convertEditorJSONToHTML(editorData))}
                                        placeholder="Maqueta aquí la plantilla de contrato para la empresa organizadora..."
                                        variables={variables}
                                    />
                                </div>

                                <div className="md:col-span-4">
                                    <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Logo de contrato</span>
                                    {data?.contractLogoDocument && (
                                        <p className="text-xs text-gray-500 mb-2">
                                            Actual: {getDocumentLabel(data.contractLogoDocument, 'logo')}
                                        </p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                        onChange={(event) => setContractLogoFile(event.target.files?.[0] ?? null)}
                                    />
                                </div>

                                <div className="md:col-span-4">
                                    <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Firma de contrato</span>
                                    {data?.contractSignatureDocument && (
                                        <p className="text-xs text-gray-500 mb-2">
                                            Actual: {getDocumentLabel(data.contractSignatureDocument, 'firma')}
                                        </p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                        onChange={(event) => setContractSignatureFile(event.target.files?.[0] ?? null)}
                                    />
                                </div>

                                <div className="md:col-span-4">
                                    <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Sello de contrato</span>
                                    {data?.contractSealDocument && (
                                        <p className="text-xs text-gray-500 mb-2">
                                            Actual: {getDocumentLabel(data.contractSealDocument, 'sello')}
                                        </p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                        onChange={(event) => setContractSealFile(event.target.files?.[0] ?? null)}
                                    />
                                </div>
                            </FormGroup>
                        )}

                        <BanksForm formik={formik} bank={bank} setBank={setBank} />
                    </div>
                </form>
            </div>
        )
    };

    return (
        <>
            {isOpen && (
                <>
                    <Page container="fluid">
                        {(mode === "Editar" && data) && getContent()}
                        {mode === "Crear" && getContent()}
                            <FormFooter
                            customClassName={"border-0 py-1"}
                            customBg={"bg-transparent"}
                            submitBtnText={mode + ' Empresa'}
                            handleCancelBtn={() => {
                                setIsOpen(false);
                                setNodeId(undefined);
                                setContractLogoFile(null);
                                setContractSignatureFile(null);
                                setContractSealFile(null);
                                formik.resetForm();
                            }}
                            handleSubmitBtn={formik.submitForm} isLoading={isLoading}
                        />
                    </Page>
                </>
            )}
        </>
    )
};

export default CorporationForm;
